import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Newspaper, Shield, Sparkles, Target, MapPin, TrendingUp, DollarSign, Users } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public Partnership Demo page — the URL you paste into your Flipp pitch email.
 *   /weekly-ads/partnership-demo?token=<FLIPP_DEMO_TOKEN>
 *
 * Renders the full unblurred Weekly Ads experience with:
 *   • "For Flipp Media Network Review" banner at top
 *   • Sponsored placement zones highlighted with dashed callout borders
 *   • Prominent "Powered by Flipp" attribution
 *   • Contact CTA footer for their partnerships team
 */
const PartnershipDemo = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || searchParams.get("demo_token") || "";

  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [flyers, setFlyers] = useState([]);
  const [flyersLoading, setFlyersLoading] = useState(false);
  const [zipCode, setZipCode] = useState("43620");
  const [activeFlyer, setActiveFlyer] = useState(null);

  // Verify the demo token against the backend gate
  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setVerified(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/weekly-ads/access-mode?demo_token=${encodeURIComponent(token)}`
        );
        const data = await res.json();
        setVerified(!!data.allowed && data.reason === "demo");
      } catch {
        setVerified(false);
      } finally {
        setVerifying(false);
      }
    })();
  }, [token]);

  // Load flyers when verified
  useEffect(() => {
    if (!verified) return;
    setFlyersLoading(true);
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/weekly-ads/flipp/flyers?postal_code=${zipCode}&demo_token=${encodeURIComponent(token)}`
        );
        const data = await res.json();
        setFlyers(data.flyers || []);
      } catch {
        setFlyers([]);
      } finally {
        setFlyersLoading(false);
      }
    })();
  }, [verified, zipCode, token]);

  const loadFlyerProducts = async (flyerId) => {
    setActiveFlyer({ id: flyerId, loading: true, products: [], merchant: "" });
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/weekly-ads/flipp/flyer/${flyerId}?demo_token=${encodeURIComponent(token)}`
      );
      const data = await res.json();
      const merchant =
        flyers.find((f) => f.flyer_id === flyerId)?.merchant || "Retailer";
      setActiveFlyer({
        id: flyerId,
        loading: false,
        products: (data.products || []).slice(0, 12),
        merchant,
      });
    } catch {
      setActiveFlyer({ id: flyerId, loading: false, products: [], merchant: "" });
    }
  };

  const monthlyEstimate = useMemo(
    () => ({
      subs: 2500,
      grossMrr: 2500 * 4.99,
      flippShareYear1: (2500 * 4.99 * 0.03).toFixed(0),
      flippShareYear2: (2500 * 4.99 * 0.06).toFixed(0),
      flippShareYear3: (2500 * 4.99 * 0.1).toFixed(0),
    }),
    []
  );

  // ── Access-denied view ──
  if (!verifying && !verified) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-6">
        <Card className="max-w-md bg-slate-800 border-slate-700" data-testid="demo-invalid">
          <CardContent className="p-8 text-center space-y-4">
            <Shield className="h-12 w-12 text-amber-400 mx-auto" />
            <h1 className="text-2xl font-bold">Partnership Demo — Access Required</h1>
            <p className="text-slate-300 text-sm">
              This page is a private preview for the Flipp Media Network partnerships
              team. Access requires a valid demo token issued directly by Foodz Lizts.
            </p>
            <p className="text-slate-400 text-xs">
              If you&apos;re on the Flipp team and reached this page in error, please reply
              to Marvin Wade&apos;s partnership inquiry email to request access.
            </p>
            <a
              href="mailto:marvin.wade@mail.com?subject=Foodz%20Lizts%20Partnership%20Demo%20Access"
              className="inline-block"
            >
              <Button size="sm" data-testid="demo-request-access">
                Request Access
              </Button>
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Loading ──
  if (verifying) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-slate-400">Verifying partnership demo access…</p>
        </div>
      </div>
    );
  }

  // ── Verified: render full demo ──
  return (
    <div className="min-h-screen bg-slate-50" data-testid="partnership-demo">
      {/* Top Banner — clearly for-Flipp branding */}
      <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-emerald-500 text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Badge className="bg-white/20 text-white border-white/30 font-semibold">
              PARTNERSHIP DEMO
            </Badge>
            <span className="text-sm font-medium">
              Prepared for the <b>Flipp Media Network</b> partnerships team
            </span>
          </div>
          <div className="text-xs opacity-90">
            Foodz Lizts × Flipp — a proposed integration preview
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Newspaper className="h-5 w-5 text-indigo-600" />
              <h1 className="text-2xl font-bold text-slate-900">Weekly Ads — Live Demo</h1>
            </div>
            <p className="text-sm text-slate-600 max-w-2xl">
              This is the exact user experience Foodz Lizts subscribers would receive
              through a licensed Flipp Media Network integration. All circular data,
              images, and pricing shown below are queried live via Flipp&apos;s data feed
              in real time for ZIP {zipCode}. Highlighted regions indicate proposed
              sponsored-placement inventory.
            </p>
          </div>
          <div className="text-right text-xs text-slate-500 space-y-1">
            <div className="flex items-center gap-1 justify-end">
              <MapPin className="h-3.5 w-3.5" /> ZIP {zipCode} · Toledo, OH
            </div>
            <div>Data source: Flipp Public Circular Feed</div>
          </div>
        </div>

        {/* Sponsored Placement Zone A — Homepage Featured Banner */}
        <SponsorshipZone
          label="Sponsored Placement — Featured Retailer Banner (Zone A)"
          note="Rotating hero placement. Estimated 40K+ monthly impressions at launch, per-CPM billed to Flipp advertisers."
        >
          {flyers[0] ? (
            <div className="flex items-center gap-4 p-4 bg-white rounded-lg border-2 border-dashed border-amber-400">
              {flyers[0].thumbnail_url ? (
                <img
                  src={flyers[0].thumbnail_url}
                  alt=""
                  className="w-24 h-24 object-cover rounded"
                />
              ) : (
                <div className="w-24 h-24 bg-slate-200 rounded" />
              )}
              <div className="flex-1">
                <div className="font-bold text-slate-900">{flyers[0].merchant}</div>
                <div className="text-sm text-slate-600">
                  {flyers[0].name} · valid {flyers[0].valid_from} → {flyers[0].valid_to}
                </div>
                <Badge className="mt-1 bg-amber-100 text-amber-800 border-amber-300">
                  Sponsored
                </Badge>
              </div>
            </div>
          ) : (
            <div className="p-4 text-sm text-slate-500">Loading featured retailer…</div>
          )}
        </SponsorshipZone>

        {/* Flyer grid */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-slate-900">
              Available Weekly Ads ({flyers.length})
            </h2>
            <Badge variant="outline" className="text-xs">
              Powered by Flipp
            </Badge>
          </div>
          {flyersLoading ? (
            <div className="text-center py-12 text-slate-500 text-sm">
              Fetching live flyers…
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {flyers.slice(0, 12).map((f) => (
                <Card
                  key={f.flyer_id}
                  className="cursor-pointer hover:shadow-md transition"
                  onClick={() => loadFlyerProducts(f.flyer_id)}
                  data-testid={`demo-flyer-${f.flyer_id}`}
                >
                  <CardContent className="p-3 space-y-2">
                    {f.thumbnail_url ? (
                      <img
                        src={f.thumbnail_url}
                        alt={f.merchant}
                        className="w-full h-32 object-cover rounded"
                      />
                    ) : (
                      <div className="w-full h-32 bg-slate-100 rounded" />
                    )}
                    <div className="text-sm font-semibold truncate">{f.merchant}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {f.valid_from} → {f.valid_to}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Sponsored Placement Zone B — In-Feed Product Card */}
        {activeFlyer && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">
                {activeFlyer.merchant} — Products
              </h3>
              <Badge variant="outline">Flyer #{activeFlyer.id}</Badge>
            </div>

            <SponsorshipZone
              label="Sponsored Placement — Featured Product Slot (Zone B)"
              note="Top-of-list product cards can be sold as premium placement to Flipp's CPG advertisers."
            >
              {activeFlyer.loading ? (
                <div className="p-4 text-sm text-slate-500">Loading products…</div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-2">
                  {activeFlyer.products.slice(0, 4).map((p, i) => (
                    <div
                      key={p.id || i}
                      className="bg-white rounded border-2 border-dashed border-amber-400 p-2 space-y-1"
                    >
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt={p.name}
                          className="w-full h-20 object-contain"
                        />
                      ) : (
                        <div className="w-full h-20 bg-slate-100 rounded" />
                      )}
                      <div className="text-xs font-medium truncate">{p.name}</div>
                      {p.price != null && (
                        <div className="text-sm font-bold text-emerald-700">
                          ${p.price.toFixed(2)}
                        </div>
                      )}
                      <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-300 h-4">
                        Sponsored
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </SponsorshipZone>

            {/* Rest of flyer products — organic (non-sponsored) */}
            {!activeFlyer.loading && activeFlyer.products.length > 4 && (
              <div>
                <div className="text-xs text-slate-500 mb-2 uppercase tracking-wide">
                  Organic in-feed placements (rest of the flyer)
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {activeFlyer.products.slice(4, 12).map((p, i) => (
                    <Card key={p.id || i}>
                      <CardContent className="p-2 space-y-1">
                        {p.image_url ? (
                          <img
                            src={p.image_url}
                            alt={p.name}
                            className="w-full h-20 object-contain"
                          />
                        ) : (
                          <div className="w-full h-20 bg-slate-100 rounded" />
                        )}
                        <div className="text-xs font-medium truncate">{p.name}</div>
                        {p.price != null && (
                          <div className="text-sm font-bold text-emerald-700">
                            ${p.price.toFixed(2)}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Partnership economics — the pitch */}
        <div className="mt-8 space-y-3">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-600" />
            Why this partnership works
          </h2>
          <div className="grid md:grid-cols-3 gap-3">
            <MetricCard
              icon={Users}
              label="Sponsored placement zones per flyer"
              value="3–5"
              hint="Featured banner, top-of-list, mid-feed, deal-of-the-day"
            />
            <MetricCard
              icon={DollarSign}
              label="Proposed Flipp share of gross MRR"
              value="3 % → 10 %"
              hint="Graduated by ARR tier (Yr 1 → Yr 3+)"
            />
            <MetricCard
              icon={Target}
              label="Cross-promo lift for retailers"
              value="Est. +18–25%"
              hint="Meal-plan-driven click-through to flyers"
            />
          </div>

          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2 text-emerald-800 font-semibold">
                <Sparkles className="h-4 w-4" />
                Illustrative revenue at 2,500 paying subscribers
              </div>
              <div className="grid md:grid-cols-3 gap-4 text-sm text-slate-700">
                <div>
                  <div className="text-xs text-slate-500">Year 1 (3 % share)</div>
                  <div className="text-2xl font-bold text-emerald-700">
                    ~${monthlyEstimate.flippShareYear1}/mo
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Year 2 (6 % share)</div>
                  <div className="text-2xl font-bold text-emerald-700">
                    ~${monthlyEstimate.flippShareYear2}/mo
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Year 3+ (10 % share)</div>
                  <div className="text-2xl font-bold text-emerald-700">
                    ~${monthlyEstimate.flippShareYear3}/mo
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Excludes advertiser CPM revenue from the sponsored placement zones
                above, which would accrue to Flipp separately per your standard
                Media Network agreement.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Footer CTA */}
        <div className="mt-8 border-t border-slate-200 pt-6 pb-12 text-center space-y-3">
          <h3 className="text-lg font-bold text-slate-900">
            Ready to move forward?
          </h3>
          <p className="text-sm text-slate-600 max-w-lg mx-auto">
            We&apos;re actively transitioning off unlicensed data sources. Reply to the
            partnership inquiry email or contact us directly to start the MSA
            conversation.
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            <a href="mailto:marvin.wade@mail.com?subject=Flipp%20%2B%20Foodz%20Lizts%20Partnership">
              <Button
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="demo-contact-founder"
              >
                Contact Marvin Wade
              </Button>
            </a>
            <a href="https://foodzlizts.com" target="_blank" rel="noreferrer">
              <Button variant="outline" data-testid="demo-visit-site">
                Visit foodzlizts.com
              </Button>
            </a>
          </div>
          <p className="text-[10px] text-slate-400 mt-6 max-w-xl mx-auto">
            All flyer data, brand imagery, and pricing shown on this page are the
            property of the respective retailers and are surfaced via Flipp&apos;s public
            feed. This preview is intended solely for Flipp Media Network&apos;s internal
            review of a proposed licensed integration and is not publicly linked
            from the consumer product.
          </p>
        </div>
      </div>
    </div>
  );
};

const SponsorshipZone = ({ label, note, children }) => (
  <div className="relative">
    <div className="absolute -top-3 left-4 z-10 px-2 py-0.5 rounded bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider shadow">
      {label}
    </div>
    <div className="border-2 border-dashed border-amber-400 bg-amber-50/40 rounded-lg pt-4 pb-2 px-2">
      {children}
      <div className="mt-2 px-2 text-[11px] text-amber-800 italic">{note}</div>
    </div>
  </div>
);

const MetricCard = ({ icon: Icon, label, value, hint }) => (
  <Card>
    <CardContent className="p-4 space-y-1">
      <div className="flex items-center gap-2 text-slate-600 text-xs">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-[11px] text-slate-500">{hint}</div>
    </CardContent>
  </Card>
);

export default PartnershipDemo;
